import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';

import paths from '@src/paths';
import { useMessage } from '@src/context/common';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import {
  createInvoiceBillingContact,
  deleteInvoiceBillingContact,
  listInvoiceBillingContacts,
  retrieveInvoiceClientSettings,
  updateInvoiceBillingContact,
  updateInvoiceClientSettings,
} from '@src/api/invoices';

const BILLING_CYCLE_OPTIONS = [
  { value: 'bi_weekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const BILLING_TERMS_OPTIONS = [
  { value: 0, label: 'Due on Receipt' },
  { value: 7, label: 'Net 7' },
  { value: 15, label: 'Net 15' },
  { value: 30, label: 'Net 30' },
  { value: 45, label: 'Net 45' },
];

const SPEND_FUNDING_OPTIONS = [
  { value: 'guru_funds_spend', label: 'Lead Gurus funds ad spend' },
  { value: 'client_funds_spend', label: 'Client funds ad spend' },
];

const CONTACT_ROLE_OPTIONS = [
  { value: 'main', label: 'Main' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'billing', label: 'Billing' },
  { value: 'tech', label: 'Technical' },
];

const createEmptyContact = () => ({
  id: null,
  first_name: '',
  last_name: '',
  email: '',
  position: '',
  role: 'secondary',
  active: true,
  receives_billing_notifications: true,
  is_editing: true,
});

const normalizeContacts = (payload) => {
  const rows = Array.isArray(payload) ? payload : payload?.results || [];
  return rows
    .filter((row) => !!row)
    .map((row) => ({
      id: row.id,
      first_name: row.first_name || '',
      last_name: row.last_name || '',
      email: row.email || '',
      position: row.position || '',
      role: row.role || 'secondary',
      active: row.active !== false,
      receives_billing_notifications: row.receives_billing_notifications !== false,
      is_editing: false,
    }));
};

const buildClientPayload = (settings) => {
  return {
    billing_cycle: settings.billing_cycle || 'bi_weekly',
    billing_terms: Number.isFinite(Number(settings.billing_terms)) ? Number(settings.billing_terms) : 7,
    spend_funding: settings.spend_funding || 'guru_funds_spend',
    billing_contact_name: settings.billing_contact_name || '',
    billing_contact_email: settings.billing_contact_email || '',
    billing_contact_phone: settings.billing_contact_phone || '',
    billing_address_line1: settings.billing_address_line1 || '',
    billing_address_line2: settings.billing_address_line2 || '',
    billing_address_city: settings.billing_address_city || '',
    billing_address_state: settings.billing_address_state || '',
    billing_address_postal_code: settings.billing_address_postal_code || '',
    billing_address_country: settings.billing_address_country || '',
  };
};

const formatDateTime = (value) => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }
  return parsed.toLocaleString();
};

const getMethodConsent = (consent, paymentMethod) => {
  if (!consent || typeof consent !== 'object') {
    return null;
  }
  const byMethod = consent.by_payment_method;
  if (byMethod && typeof byMethod === 'object') {
    return byMethod[paymentMethod] || null;
  }
  if (consent.payment_method === paymentMethod) {
    return consent;
  }
  return null;
};

const isContactEmpty = (contact) =>
  !String(contact.first_name || '').trim() &&
  !String(contact.last_name || '').trim() &&
  !String(contact.email || '').trim() &&
  !String(contact.position || '').trim();

export default function InvoiceSettingsPage() {
  const navigate = useNavigate();
  const message = useMessage();
  const { options: clientOptions, loading: clientsLoading, handleInputChange } = useClientAutocomplete();

  const [selectedClient, setSelectedClient] = useState(null);
  const [settings, setSettings] = useState({});
  const [contacts, setContacts] = useState([]);
  const [removedContactIds, setRemovedContactIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const clientId = selectedClient?.value || null;

  const loadClientData = useCallback((nextClientId) => {
    if (!nextClientId) {
      setSettings({});
      setContacts([]);
      setRemovedContactIds([]);
      return;
    }
    setLoading(true);
    setError('');
    Promise.all([
      retrieveInvoiceClientSettings(nextClientId),
      listInvoiceBillingContacts(nextClientId, { page_size: 200 }),
    ])
      .then(([clientRes, contactsRes]) => {
        const client = clientRes?.data || {};
        setSettings({
          billing_cycle: client.billing_cycle || 'bi_weekly',
          billing_terms: client.billing_terms ?? 7,
          spend_funding: client.spend_funding || 'guru_funds_spend',
          fees: client.fees ?? '',
          billing_contact_name: client.billing_contact_name || '',
          billing_contact_email: client.billing_contact_email || '',
          billing_contact_phone: client.billing_contact_phone || '',
          billing_address_line1: client.billing_address_line1 || '',
          billing_address_line2: client.billing_address_line2 || '',
          billing_address_city: client.billing_address_city || '',
          billing_address_state: client.billing_address_state || '',
          billing_address_postal_code: client.billing_address_postal_code || '',
          billing_address_country: client.billing_address_country || '',
          stripe_customer_id: client.stripe_customer_id || '',
          stripe_autopay_consent: client.stripe_autopay_consent || {},
        });
        setContacts(normalizeContacts(contactsRes?.data));
        setRemovedContactIds([]);
      })
      .catch((err) => {
        console.error('Failed to load invoice settings', err);
        setError('Unable to load client billing settings.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!clientId) {
      return;
    }
    loadClientData(clientId);
  }, [clientId, loadClientData]);

  const updateSetting = useCallback((field) => (event) => {
    const value = event?.target?.value;
    setSettings((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateContact = useCallback((index, field, value) => {
    setContacts((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);

  const removeContact = useCallback((index) => {
    setContacts((prev) => {
      const row = prev[index];
      if (row?.id) {
        setRemovedContactIds((ids) => (ids.includes(row.id) ? ids : [...ids, row.id]));
      }
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  }, []);

  const addContact = useCallback(() => {
    setContacts((prev) => [...prev, createEmptyContact()]);
  }, []);

  const enableContactEditing = useCallback((index) => {
    setContacts((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], is_editing: true };
      return next;
    });
  }, []);

  const saveSettings = useCallback(() => {
    if (!clientId) {
      message.error('Select a client first.');
      return;
    }

    const activeContacts = contacts.filter((contact) => !isContactEmpty(contact));
    const invalidContact = activeContacts.find((contact) => !String(contact.email || '').trim());
    if (invalidContact) {
      message.error('Each billing contact requires an email address.');
      return;
    }

    const impliedRemovedIds = contacts
      .filter((contact) => contact.id && isContactEmpty(contact))
      .map((contact) => contact.id);

    const idsToDelete = Array.from(new Set([...removedContactIds, ...impliedRemovedIds]));

    setSaving(true);
    setError('');

    updateInvoiceClientSettings(clientId, buildClientPayload(settings))
      .then(() => {
        const operations = [];
        activeContacts.forEach((contact) => {
          const payload = {
            client: clientId,
            first_name: String(contact.first_name || '').trim(),
            last_name: String(contact.last_name || '').trim(),
            email: String(contact.email || '').trim(),
            position: String(contact.position || '').trim(),
            role: contact.role || 'secondary',
            active: contact.active !== false,
            receives_billing_notifications: contact.receives_billing_notifications !== false,
          };
          if (contact.id) {
            operations.push(updateInvoiceBillingContact(contact.id, payload));
          } else {
            operations.push(createInvoiceBillingContact(payload));
          }
        });

        idsToDelete.forEach((id) => {
          operations.push(deleteInvoiceBillingContact(id));
        });

        return Promise.all(operations);
      })
      .then(() => {
        message.success('Invoice settings saved.');
        loadClientData(clientId);
      })
      .catch((err) => {
        console.error('Failed to save invoice settings', err);
        setError('Unable to save invoice settings. Please fix any validation errors and try again.');
        message.error('Failed to save invoice settings.');
      })
      .finally(() => setSaving(false));
  }, [clientId, contacts, loadClientData, message, removedContactIds, settings]);

  const contactCountLabel = useMemo(() => {
    const count = contacts.filter((contact) => !isContactEmpty(contact)).length;
    return `${count} billing contact${count === 1 ? '' : 's'}`;
  }, [contacts]);

  const cardConsent = useMemo(
    () => getMethodConsent(settings.stripe_autopay_consent, 'card'),
    [settings.stripe_autopay_consent],
  );
  const bankConsent = useMemo(
    () => getMethodConsent(settings.stripe_autopay_consent, 'us_bank_account'),
    [settings.stripe_autopay_consent],
  );

  return (
    <Box p={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between" mb={3}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(paths.dashboard.invoices.list)}>
            Back to invoices
          </Button>
          <Typography variant="h4">Invoice Settings</Typography>
        </Stack>
        <LoadingButton
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={saveSettings}
          loading={saving}
          disabled={!clientId || loading}
        >
          Save settings
        </LoadingButton>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Client"
          action={
            <IconButton onClick={() => loadClientData(clientId)} disabled={!clientId || loading} aria-label="refresh client billing settings">
              <RefreshIcon />
            </IconButton>
          }
        />
        <Divider />
        <CardContent>
          <Autocomplete
            options={clientOptions}
            loading={clientsLoading}
            value={selectedClient}
            onChange={(event, value) => {
              setSelectedClient(value);
              if (!value) {
                loadClientData(null);
              }
            }}
            onInputChange={handleInputChange}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => <TextField {...params} label="Select client" placeholder="Search clients" fullWidth />}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardHeader title="Stripe Billing Profile" />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Stripe customer
              </Typography>
              <Typography variant="body1">
                {settings.stripe_customer_id || 'Not connected yet'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Card auto-charge consent
              </Typography>
              <Typography variant="body1">
                {cardConsent?.authorized ? 'Authorized' : 'Not authorized'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Authorized at: {formatDateTime(cardConsent?.authorized_at)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Invoice: {cardConsent?.invoice_number || '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Authorized by: {cardConsent?.email || cardConsent?.name || '—'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Bank auto-charge consent
              </Typography>
              <Typography variant="body1">
                {bankConsent?.authorized ? 'Authorized' : 'Not authorized'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Authorized at: {formatDateTime(bankConsent?.authorized_at)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Invoice: {bankConsent?.invoice_number || '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Authorized by: {bankConsent?.email || bankConsent?.name || '—'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader title="Billing Configuration" />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Billing cycle</InputLabel>
                    <Select
                      label="Billing cycle"
                      value={settings.billing_cycle || 'bi_weekly'}
                      onChange={updateSetting('billing_cycle')}
                      disabled={!clientId || loading}
                    >
                      {BILLING_CYCLE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Billing terms</InputLabel>
                    <Select
                      label="Billing terms"
                      value={Number(settings.billing_terms ?? 7)}
                      onChange={updateSetting('billing_terms')}
                      disabled={!clientId || loading}
                    >
                      {BILLING_TERMS_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Client fees"
                    fullWidth
                    value={settings.fees ?? ''}
                    disabled
                    helperText="Read-only from client.fees."
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Spend funding</InputLabel>
                    <Select
                      label="Spend funding"
                      value={settings.spend_funding || 'guru_funds_spend'}
                      onChange={updateSetting('spend_funding')}
                      disabled={!clientId || loading}
                    >
                      {SPEND_FUNDING_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader title="Primary Billing Contact" />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Name"
                    fullWidth
                    value={settings.billing_contact_name || ''}
                    onChange={updateSetting('billing_contact_name')}
                    disabled={!clientId || loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    fullWidth
                    value={settings.billing_contact_email || ''}
                    onChange={updateSetting('billing_contact_email')}
                    disabled={!clientId || loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone"
                    fullWidth
                    value={settings.billing_contact_phone || ''}
                    onChange={updateSetting('billing_contact_phone')}
                    disabled={!clientId || loading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address line 1"
                    fullWidth
                    value={settings.billing_address_line1 || ''}
                    onChange={updateSetting('billing_address_line1')}
                    disabled={!clientId || loading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address line 2"
                    fullWidth
                    value={settings.billing_address_line2 || ''}
                    onChange={updateSetting('billing_address_line2')}
                    disabled={!clientId || loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="City"
                    fullWidth
                    value={settings.billing_address_city || ''}
                    onChange={updateSetting('billing_address_city')}
                    disabled={!clientId || loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="State"
                    fullWidth
                    value={settings.billing_address_state || ''}
                    onChange={updateSetting('billing_address_state')}
                    disabled={!clientId || loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Postal code"
                    fullWidth
                    value={settings.billing_address_postal_code || ''}
                    onChange={updateSetting('billing_address_postal_code')}
                    disabled={!clientId || loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Country"
                    fullWidth
                    value={settings.billing_address_country || ''}
                    onChange={updateSetting('billing_address_country')}
                    disabled={!clientId || loading}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Secondary Billing Contacts"
              subheader="Stored on Client POCs. To receive invoice emails, 'Receives billing notifications' must be checked."
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {contactCountLabel}
                  </Typography>
                  <Button startIcon={<AddCircleOutlineIcon />} onClick={addContact} disabled={!clientId || loading}>
                    Add contact
                  </Button>
                </Stack>
              }
            />
            <Divider />
            <CardContent>
              {!clientId ? (
                <Typography color="text.secondary">Select a client to manage billing contacts.</Typography>
              ) : null}
              {clientId && !contacts.length ? (
                <Alert severity="info">No secondary billing contacts yet. Add one to include them on invoice reminders.</Alert>
              ) : null}
              <Stack spacing={2} mt={contacts.length ? 0 : 2}>
                {contacts.map((contact, index) => (
                  <Paper key={contact.id || `new-${index}`} variant="outlined" sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 1.25,
                        alignItems: 'center',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'minmax(120px,1fr) minmax(120px,1fr) minmax(220px,1.4fr) minmax(140px,1fr) minmax(110px,0.8fr) auto auto auto',
                        },
                      }}
                    >
                      <Box>
                        <TextField
                          label="First name"
                          fullWidth
                          size="small"
                          value={contact.first_name}
                          onChange={(event) => updateContact(index, 'first_name', event.target.value)}
                          disabled={loading || saving || !contact.is_editing}
                        />
                      </Box>
                      <Box>
                        <TextField
                          label="Last name"
                          fullWidth
                          size="small"
                          value={contact.last_name}
                          onChange={(event) => updateContact(index, 'last_name', event.target.value)}
                          disabled={loading || saving || !contact.is_editing}
                        />
                      </Box>
                      <Box>
                        <TextField
                          label="Email"
                          fullWidth
                          size="small"
                          value={contact.email}
                          onChange={(event) => updateContact(index, 'email', event.target.value)}
                          disabled={loading || saving || !contact.is_editing}
                        />
                      </Box>
                      <Box>
                        <TextField
                          label="Position"
                          fullWidth
                          size="small"
                          value={contact.position}
                          onChange={(event) => updateContact(index, 'position', event.target.value)}
                          disabled={loading || saving || !contact.is_editing}
                        />
                      </Box>
                      <Box>
                        <FormControl fullWidth size="small">
                          <InputLabel>Role</InputLabel>
                          <Select
                            label="Role"
                            value={contact.role || 'secondary'}
                            onChange={(event) => updateContact(index, 'role', event.target.value)}
                            disabled={loading || saving || !contact.is_editing}
                          >
                            {CONTACT_ROLE_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      <FormControlLabel
                        sx={{ m: 0, whiteSpace: 'nowrap' }}
                        control={(
                          <Checkbox
                            size="small"
                            checked={contact.receives_billing_notifications !== false}
                            onChange={(event) =>
                              updateContact(index, 'receives_billing_notifications', event.target.checked)
                            }
                            disabled={loading || saving || !contact.is_editing}
                          />
                        )}
                        label={<Typography variant="body2">Receives billing notifications</Typography>}
                      />
                      <FormControlLabel
                        sx={{ m: 0, whiteSpace: 'nowrap' }}
                        control={(
                          <Checkbox
                            size="small"
                            checked={contact.active !== false}
                            onChange={(event) => updateContact(index, 'active', event.target.checked)}
                            disabled={loading || saving || !contact.is_editing}
                          />
                        )}
                        label={<Typography variant="body2">Active</Typography>}
                      />
                      <Stack direction="row" spacing={0.5} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                        {!contact.is_editing ? (
                          <IconButton
                            size="small"
                            onClick={() => enableContactEditing(index)}
                            color="primary"
                            disabled={loading || saving}
                            aria-label="edit contact"
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                        <IconButton size="small" onClick={() => removeContact(index)} color="error" disabled={loading || saving}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
